'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Mail, Loader2, CheckCircle2 } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Button } from '@/components/ui/button'
import { siteConfig, productStats, stats } from '@/lib/constants'
import { cn } from '@/lib/utils'

interface CTASectionProps {
  variant?: 'light' | 'dark'
  title?: string
  description?: string
}

export function CTASection({
  variant = 'light',
  title = 'Put your store’s knowledge to work.',
  description = `Join ${stats.brands} brands on Buddify. Give shoppers a knowledgeable AI teammate, then grow your catalog through brand partnerships.`,
}: CTASectionProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const isDark = variant === 'dark'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')

    // Simulate API call - replace with actual newsletter subscription
    await new Promise(resolve => setTimeout(resolve, 1000))

    // TODO: Integrate with actual email service (e.g., Mailchimp, ConvertKit, etc.)
    setStatus('success')
    setEmail('')
  }

  return (
    <section
      className={cn(
        'relative overflow-hidden py-20 md:py-28 border-t',
        isDark
          ? 'bg-page border-line'
          : 'bg-panel border-line'
      )}
    >
      <Container className="relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-5xl mx-auto"
        >
          <h2
            className={cn(
              'mb-6',
              'text-display-1',
              'text-ink'
            )}
          >
            {title}
          </h2>
          <p className={cn('text-xl mb-10 max-w-3xl mx-auto', 'text-muted')}>
            {description}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 mb-12">
            <Button
              href={productStats.aiAssistant.appStoreUrl}
              external
              variant="ai"
              size="xl"
              className="w-full sm:w-auto justify-center"
            >
              Try Buddy AI
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              href={siteConfig.links.shopifyApp}
              external
              size="xl"
              className="w-full sm:w-auto justify-center"
            >
              Try Brand Partnerships
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className={cn('flex-1 h-px', 'bg-raised')} />
            <span className={cn('text-sm font-medium', 'text-muted')}>
              or stay updated
            </span>
            <div className={cn('flex-1 h-px', 'bg-raised')} />
          </div>

          {/* Email signup */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className={cn('text-sm mb-4', 'text-muted')}>
              Get e-commerce tips, product updates, and exclusive offers delivered to your inbox.
            </p>

            {status === 'success' ? (
              <div className={cn(
                'inline-flex items-center gap-2 rounded-full px-6 py-3',
                isDark ? 'bg-green-500/10 text-brand-green' : 'bg-brand-green-soft text-brand-green'
              )}>
                <CheckCircle2 className={cn('w-5 h-5', 'text-brand-green')} />
                <span>Thanks! You&apos;re subscribed.</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
                <div className="relative w-full sm:flex-1">
                  <Mail className={cn('absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5', 'text-muted')} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={cn(
                      'w-full pl-12 pr-4 py-3 rounded-full outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500',
                      isDark
                        ? 'bg-panel border border-line text-ink placeholder:text-muted'
                        : 'bg-page border border-line text-ink placeholder:text-muted'
                    )}
                    required
                    disabled={status === 'loading'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading' || !email}
                  className="w-full sm:w-auto px-6 py-3 rounded-full bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </form>
            )}

            <p className={cn('text-xs mt-3', 'text-muted')}>
              No spam. Unsubscribe anytime.
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  )
}
